import { z } from 'zod';
import { dateSchema, ulidSchema } from './common.js';
import {
  DATA_CONFIDENCE,
  PLANTING_PATTERNS,
  REMOVAL_REASONS,
  TREE_STATUSES,
} from '../enums.js';

/** Request contracts for the tree and block endpoints. */

export const dataConfidenceSchema = z.enum(DATA_CONFIDENCE);

export const plantTreeSchema = z.object({
  positionId: ulidSchema,
  plantedOn: dateSchema,
  varietyId: ulidSchema.optional(),
  seedSource: z.string().trim().max(200).optional(),
  nurseryBatch: z.string().trim().max(100).optional(),
  seedlingAgeMonths: z.number().int().min(0).max(120).optional(),
  isYieldSample: z.boolean().optional(),
  notes: z.string().max(4000).optional(),
  /**
   * Defaults to `recorded`. The CSV import passes `estimated` for the existing
   * 625, whose planting dates nobody actually wrote down — without the flag
   * those guesses become facts within a year (§16 item 3).
   */
  plantedOnConfidence: dataConfidenceSchema.optional(),
  attachmentIds: z.array(ulidSchema).max(20).optional(),
});

export const updateTreeSchema = z
  .object({
    varietyId: ulidSchema.nullable().optional(),
    seedSource: z.string().trim().max(200).nullable().optional(),
    nurseryBatch: z.string().trim().max(100).nullable().optional(),
    plantedOn: dateSchema.optional(),
    firstSpadixOn: dateSchema.nullable().optional(),
    firstFruitSetOn: dateSchema.nullable().optional(),
    firstHarvestOn: dateSchema.nullable().optional(),
    plantedOnConfidence: dataConfidenceSchema.optional(),
    milestoneConfidence: dataConfidenceSchema.optional(),
    isYieldSample: z.boolean().optional(),
    profileAttachmentId: ulidSchema.nullable().optional(),
    notes: z.string().max(4000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');

/**
 * Marking a tree dead removes it from every future yield calculation, so §17.5
 * asks for a two-person rule. The enforceable half is here: a reason is
 * mandatory and a photo is strongly expected.
 */
export const markDeadSchema = z.object({
  reason: z.enum(REMOVAL_REASONS),
  occurredOn: dateSchema,
  notes: z.string().max(4000).optional(),
  attachmentIds: z.array(ulidSchema).max(20).optional(),
});

export const replaceTreeSchema = z.object({
  removedOn: dateSchema,
  removalReason: z.enum(REMOVAL_REASONS),
  notes: z.string().max(4000).optional(),
  /** Omit to leave the position vacant and replant later. */
  replant: plantTreeSchema.omit({ positionId: true }).optional(),
});

export const transplantSchema = z.object({
  toPositionId: ulidSchema,
  occurredOn: dateSchema,
});

export const treeFiltersSchema = z.object({
  site: ulidSchema.optional(),
  block: ulidSchema.optional(),
  variety: ulidSchema.optional(),
  status: z.enum(TREE_STATUSES).optional(),
  sample: z.enum(['true', 'false']).optional(),
  includeRemoved: z.enum(['true', 'false']).optional(),
  cursor: z.string().max(512).optional(),
  limit: z.string().optional(),
});

export const createBlockSchema = z.object({
  siteId: ulidSchema,
  /** Omit to take the next free code in the site. */
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{1,2}$/, 'Block code must be A–Z or AA–ZZ')
    .optional(),
  name: z.string().trim().max(120).optional(),
  soilType: z.string().trim().max(120).optional(),
  drainage: z.string().trim().max(120).optional(),
  /** 7.5–9 m triangular is typical for tall varieties. */
  spacingM: z.number().positive().max(30).optional(),
  plantingPattern: z.enum(PLANTING_PATTERNS).optional(),
  areaHectares: z.number().positive().max(100000).optional(),
  plantedOn: dateSchema.optional(),
});

export const updateBlockSchema = createBlockSchema
  .omit({ siteId: true, code: true })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
